import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { RefreshCw } from 'lucide-react';
import { isTauri } from '../utils/tauri';
import { subscribeTauriEvent } from '../utils/safeTauriEvent';
import { useVaultMasterStore, refreshVaultMaster } from '../store/vaultStore';
import { checkForAppUpdateNow } from '../services/vaultUpdateRequired';
import './Vault.css';

/** How often the open vault re-reads whether a newer build is needed. */
const RECHECK_MS = 5 * 60 * 1000;

/**
 * The vault version gate's line above the item list (Standalone vault board,
 * card 1.1). Two causes, one message:
 *
 * - the keyblock itself is newer (`vault_master_status.updateRequired`) —
 *   nothing opens until the update; or
 * - items pulled from another device are newer (`vault_sync_status
 *   .updateRequired`) — they wait, untouched, and the rest keeps working.
 *
 * Not dismissible: it disappears by itself once the update is installed.
 */
export default function VaultUpdateRequiredBanner() {
  const { t } = useTranslation();
  const master = useVaultMasterStore((s) => s.master);
  const [pulledNewer, setPulledNewer] = useState(false);
  const [outcome, setOutcome] = useState('');

  useEffect(() => {
    if (!isTauri()) return undefined;
    let alive = true;
    const read = async () => {
      // The locked panel mounts no other reader of the master status, and a
      // newer keyblock always leaves the vault locked (Sandbox, 04.10.2026).
      refreshVaultMaster();
      try {
        const status = await invoke('vault_sync_status');
        if (alive) setPulledNewer(!!status?.updateRequired);
      } catch {
        // A status read that fails says nothing about versions; keep the last answer.
      }
    };
    read();
    const timer = setInterval(read, RECHECK_MS);
    window.addEventListener('vault-master-changed', read);
    // The sync engine says when pulled rows start (or stop) needing a newer
    // build; a skipped row emits no `vault:changed`.
    const sub = subscribeTauriEvent('vault:update-required', read);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener('vault-master-changed', read);
      sub?.unlisten?.();
    };
  }, []);

  const keyblockNewer = !!master?.updateRequired;
  if (!isTauri() || (!keyblockNewer && !pulledNewer)) return null;

  const check = async () => setOutcome(await checkForAppUpdateNow());

  return (
    <div className="vault-pass-banner vault-pass-banner--warn" data-testid="vault-update-required-banner">
      <RefreshCw size={15} className="vault-pass-banner-icon" />
      <div className="vault-pass-banner-texts">
        <span className="vault-pass-banner-title">
          {keyblockNewer
            ? t('vault.updateRequiredBannerTitle', 'Update Apiboo to open this vault')
            : t('vault.updateRequiredItemsBannerTitle', 'Some vault changes need a newer Apiboo')}
        </span>
        <span className="vault-pass-banner-text">
          {outcome === 'none'
            ? t('vault.updateRequiredNoUpdate', 'No update is offered for this computer yet. Try again later — your vault stays as it is until then.')
            : outcome === 'error'
              ? t('vault.updateRequiredCheckFailed', "Couldn't check for updates. Check your connection and try again.")
              : keyblockNewer
                ? t('vault.updateRequiredBannerBody', 'It was saved by a newer version. Your passphrase is not the problem, and nothing has been changed or lost.')
                : t('vault.updateRequiredItemsBannerBody', 'Another device saved items in a newer format. They are kept safely and appear after the update; everything else works as usual.')}
        </span>
      </div>
      <div className="vault-pass-banner-actions">
        <button
          type="button"
          className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors modern-button text-sm"
          onClick={check}
        >
          {t('vault.updateRequiredCheck', 'Check for updates')}
        </button>
      </div>
    </div>
  );
}
